import React from "react";
import { Check } from "@tamagui/lucide-icons";
import { Popover, styled, Text, XStack, YStack } from "tamagui";

interface Option {
  name: string;
  value: string;
}
type Options = Option[];

const CheckIcon = styled(Check, {
  name: "CheckIcon",
  variants: {
    variant: {
      default: {},
    },
    selected: {
      true: {
        color: "$lightblue-700",
      },
      false: {
        color: "$gray-primary-900",
      },
    },
  } as const,
  defaultVariants: {
    variant: "default",
  },
});

export const MultiSelect: React.FC<{
  trigger?: React.ReactNode;
  options: Options;
  onChange: (value: string[]) => void;
  value: string[];
  maxSelectedCount?: number;
}> = ({ options, value, onChange, trigger, maxSelectedCount }) => {
  const toggleSelect = (v: string) => {
    if (value.includes(v)) {
      onChange(value.filter((r) => r !== v));
    } else {
      if (maxSelectedCount && value.length >= maxSelectedCount) {
        return;
      }
      onChange([...value, v]);
    }
  };

  return (
    <Popover data-id={"multi-select-popover"}>
      <Popover.Trigger>{trigger}</Popover.Trigger>
      <Popover.Content
        data-id={"multi-select-content"}
        borderColor={"$gray-200"}
        borderWidth={"$0.2"}
        backgroundColor={"white"}
        borderRadius="$2"
      >
        <Popover.ScrollView
          contentContainerStyle={{ maxHeight: 500, padding: 8 }}
        >
          <YStack gap={"$2"}>
            {options.map((item, index) => {
              const selected = value.includes(item.value);
              const isLimited =
                maxSelectedCount && value.length >= maxSelectedCount;
              const isDisabled = !selected && isLimited;
              return (
                <XStack
                  data-id="multi-select-option"
                  key={index}
                  onPress={() => toggleSelect(item.value)}
                  cursor={isDisabled ? "not-allowed" : "pointer"}
                  disabled={isDisabled ? true : false}
                  opacity={isDisabled ? 0.7 : 1}
                  flexDirection="row"
                  gap={8}
                  height={"$4"}
                  flexShrink={1}
                  style={{
                    ...(options.length - 1 !== index && {
                      borderBottomWidth: 1,
                      borderBottomColor: "$gray-100",
                    }),
                  }}
                >
                  <Text
                    fontFamily={"$subtitle"}
                    fontWeight={"$4"}
                    fontSize={"$2"}
                    lineHeight={"$2"}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    width={"100%"}
                  >
                    {item.name}
                  </Text>

                  <Text numberOfLines={1}>
                    {selected ? (
                      <CheckIcon selected={selected} size={20} />
                    ) : null}
                  </Text>
                </XStack>
              );
            })}
          </YStack>
        </Popover.ScrollView>
      </Popover.Content>
    </Popover>
  );
};
